import { useState } from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import LeaderInfoModal from './LeaderInfoModal';

interface Leader {
  name: string;
  title: string;
  avatar?: string;
  vision: string;
  attentes: string;
  pagePRIsME?: string;
  email?: string;
  departmentKey?: string;
}

interface LeadersSectionProps {
  leaders: Leader[];
  title?: string;
}

export default function LeadersSection({ leaders, title = "Nos Leaders" }: LeadersSectionProps) {
  const [selectedLeader, setSelectedLeader] = useState<Leader | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLeaderClick = (leader: Leader) => {
    setSelectedLeader(leader);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedLeader(null), 300);
  };

  return (
    <>
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-3 md:p-4 mb-5 border border-indigo-200 shadow-md">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
            <Users className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-lg md:text-xl font-bold text-indigo-900">{title}</h2>
        </div>
        
        <div className="flex flex-nowrap items-start justify-center gap-3 overflow-x-auto pb-1">
          {leaders.map((leader, index) => (
            <motion.div
              key={leader.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ scale: 1.04, y: -4 }}
              onClick={() => handleLeaderClick(leader)}
              className="flex w-24 shrink-0 cursor-pointer flex-col items-center group md:w-28"
            >
              {leader.avatar && (
                <div className="relative mb-1.5">
                  <div className="h-14 w-14 rounded-full ring-[3px] ring-indigo-300 group-hover:ring-indigo-500 transition-all duration-300 overflow-hidden shadow-md md:h-16 md:w-16">
                    <img
                      src={leader.avatar}
                      alt={leader.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-green-500">
                    <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
                  </div>
                </div>
              )}
              <h3 className="text-xs font-bold text-indigo-900 text-center mb-0.5 group-hover:text-indigo-700 transition-colors leading-tight">
                {leader.name}
              </h3>
              <p className="text-[10px] text-indigo-600 text-center group-hover:text-indigo-500 transition-colors leading-tight">
                {leader.title}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {selectedLeader && (
        <LeaderInfoModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          leader={selectedLeader}
          feedbackContextLevel="DEPARTMENT"
          feedbackContextLabel={`Service & Direction TI – ${selectedLeader.name}`}
        />
      )}
    </>
  );
}








